const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every Two Weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hour24 = Math.floor(index / 2);
  const minute = index % 2 === 0 ? 0 : 30;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  const minuteText = String(minute).padStart(2, '0');
  return {
    value: `${String(hour24).padStart(2, '0')}:${minuteText}`,
    label: `${hour12}:${minuteText} ${period}`,
  };
});

const controlStyle = {
  width: '100%',
  padding: '12px',
  border: '3px solid #ffffff',
  borderRadius: 12,
  background: '#ffffff',
  color: '#000000',
  fontWeight: 900,
  boxSizing: 'border-box',
};

const fieldStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  minWidth: 0,
};

const labelStyle = {
  fontWeight: 900,
  textTransform: 'uppercase',
};

const groupStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 10,
};

const previewButtonStyle = {
  minHeight: 44,
  padding: '10px 16px',
  border: '3px solid #E8761D',
  borderRadius: 12,
  background: '#000000',
  color: '#E8C98F',
  fontWeight: 900,
  cursor: 'pointer',
  textTransform: 'uppercase',
};

const formatPreviewDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return String(value || '');
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const setAppointmentField = (setAppointmentDetails, field, value) => {
  setAppointmentDetails((current) => ({ ...current, [field]: value }));
};

const setRecurrenceField = (setRecurrenceSettings, field, value) => {
  setRecurrenceSettings((current) => ({ ...current, [field]: value }));
};

const RecurringTimeSelect = ({ id, label, value, onChange }) => (
  <label htmlFor={id} style={fieldStyle}>
    <span style={labelStyle}>{label}</span>
    <select id={id} style={controlStyle} value={value || ''} onChange={onChange}>
      <option value="">-- SELECT TIME --</option>
      {TIME_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const RecurringAppointmentControls = ({
  appointment,
  recurrence,
  setAppointmentDetails,
  setRecurrenceSettings,
  onDatePreview,
  datePreviewDates = [],
  datePreviewLoading = false,
  datePreviewError = null,
  styles = {},
}) => {
  const hasEndDate = recurrence?.end_date !== undefined && recurrence?.end_date !== null;
  const hasCount = recurrence?.count !== undefined && recurrence?.count !== null;
  const endingCondition = hasEndDate && !hasCount ? 'date' : 'count';

  const selectCountEnding = () => {
    setRecurrenceSettings((current) => ({
      ...current,
      count: current.count ?? '',
      end_date: null,
    }));
  };

  const selectDateEnding = () => {
    setRecurrenceSettings((current) => ({
      ...current,
      count: null,
      end_date: current.end_date ?? '',
    }));
  };

  const changeCount = (event) => {
    const rawValue = event.target.value;
    if (rawValue === '') {
      setRecurrenceSettings((current) => ({ ...current, count: '', end_date: null }));
      return;
    }

    const count = Number(rawValue);
    if (!Number.isInteger(count) || count < 1 || count > 100) return;
    setRecurrenceSettings((current) => ({ ...current, count, end_date: null }));
  };

  const changeEndDate = (event) => {
    setRecurrenceSettings((current) => ({
      ...current,
      count: null,
      end_date: event.target.value,
    }));
  };

  return (
    <section
      aria-label="Recurring appointment schedule"
      style={styles.section || {
        border: '3px solid #E8E2DA',
        borderRadius: 14,
        background: '#365C91',
        color: '#ffffff',
        padding: 12,
        boxSizing: 'border-box',
      }}
    >
      <div style={styles.sectionTitle || { ...labelStyle, marginBottom: 10 }}>
        Schedule / Recurring Schedule
      </div>

      <div style={groupStyle}>
        <label htmlFor="recurring-frequency" style={fieldStyle}>
          <span style={labelStyle}>Frequency</span>
          <select
            id="recurring-frequency"
            style={controlStyle}
            value={recurrence?.frequency || ''}
            onChange={(event) => setRecurrenceField(
              setRecurrenceSettings,
              'frequency',
              event.target.value
            )}
          >
            <option value="">-- SELECT FREQUENCY --</option>
            {FREQUENCY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label htmlFor="recurring-anchor-date" style={fieldStyle}>
          <span style={labelStyle}>Starting Date</span>
          <input
            id="recurring-anchor-date"
            type="date"
            style={controlStyle}
            value={recurrence?.anchor_date || ''}
            onChange={(event) => setRecurrenceField(
              setRecurrenceSettings,
              'anchor_date',
              event.target.value
            )}
          />
        </label>

      </div>

      <fieldset
        style={{
          border: '3px solid #E8E2DA',
          borderRadius: 12,
          margin: '12px 0 0',
          padding: 10,
          minWidth: 0,
        }}
      >
        <legend style={labelStyle}>Ending Condition</legend>
        <div style={groupStyle}>
          <label style={fieldStyle}>
            <span style={{ fontWeight: 900 }}>
              <input
                type="radio"
                name="recurring-ending-condition"
                value="count"
                checked={endingCondition === 'count'}
                onChange={selectCountEnding}
              />{' '}
              End After
            </span>
            <input
              type="number"
              inputMode="numeric"
              min="1"
              max="100"
              step="1"
              style={controlStyle}
              value={endingCondition === 'count' ? recurrence?.count ?? '' : ''}
              disabled={endingCondition !== 'count'}
              onChange={changeCount}
              aria-label="Number of recurring appointments"
            />
            <span style={{ fontWeight: 800 }}>Maximum 100 appointments</span>
          </label>

          <label style={fieldStyle}>
            <span style={{ fontWeight: 900 }}>
              <input
                type="radio"
                name="recurring-ending-condition"
                value="date"
                checked={endingCondition === 'date'}
                onChange={selectDateEnding}
              />{' '}
              End On Date
            </span>
            <input
              type="date"
              style={controlStyle}
              value={endingCondition === 'date' ? recurrence?.end_date || '' : ''}
              disabled={endingCondition !== 'date'}
              onChange={changeEndDate}
              aria-label="Recurring appointment ending date"
            />
          </label>
        </div>
      </fieldset>

      {datePreviewError ? (
        <div role="alert" style={{ marginTop: 12, fontWeight: 900, textAlign: 'center' }}>
          {datePreviewError}
        </div>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', width: '100%', marginTop: 14 }}>
        <button
          type="button"
          className="jf-recurring-mobile-action"
          style={{
            ...previewButtonStyle,
            width: '100%',
            opacity: datePreviewLoading ? 0.65 : 1,
            cursor: datePreviewLoading ? 'not-allowed' : 'pointer',
            boxSizing: 'border-box',
          }}
          disabled={datePreviewLoading}
          onMouseEnter={(event) => {
            if (!datePreviewLoading) {
              event.currentTarget.style.background = '#FFC107';
              event.currentTarget.style.color = '#000000';
            }
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = '#000000';
            event.currentTarget.style.color = '#E8C98F';
          }}
          onClick={onDatePreview}
        >
          {datePreviewLoading ? 'PREVIEWING DATES…' : 'PREVIEW RECURRENCE DATES'}
        </button>
      </div>

      {datePreviewDates.length ? (
        <div style={{ marginTop: 12, border: '3px solid #E8761D', borderRadius: 12, background: '#000000', color: '#ffffff', padding: 12, boxSizing: 'border-box' }}>
          <div style={{ color: '#E8C98F', fontWeight: 900, textAlign: 'center', marginBottom: 8 }}>
            DATE PREVIEW — {datePreviewDates.length} OCCURRENCE{datePreviewDates.length === 1 ? '' : 'S'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto', paddingRight: 4 }}>
            {datePreviewDates.map((date, index) => (
              <div key={`${date}-${index}`} style={{ minWidth: 0, whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 900, fontSize: 'clamp(12px, 4vw, 16px)' }}>
                #{index + 1} — {formatPreviewDate(date)}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8, textAlign: 'center', fontWeight: 800 }}>
            Dates only. Complete the appointment and run the authoritative review before creation.
          </div>
        </div>
      ) : null}

      <div style={{ ...groupStyle, marginTop: 12 }}>
        <RecurringTimeSelect
          id="recurring-start-time"
          label="Start Time"
          value={appointment?.start_time}
          onChange={(event) => setAppointmentField(
            setAppointmentDetails,
            'start_time',
            event.target.value
          )}
        />
        <RecurringTimeSelect
          id="recurring-end-time"
          label="End Time"
          value={appointment?.end_time}
          onChange={(event) => setAppointmentField(
            setAppointmentDetails,
            'end_time',
            event.target.value
          )}
        />
      </div>

    </section>
  );
};

export default RecurringAppointmentControls;
